import { z } from 'zod';

import { pageMetaSchema } from '@/lib/api/pagination';

import type { ActivityEvent, Device, DeviceCommand, DeviceTelemetry } from './types';

export const deviceStatusSchema = z.enum(['active', 'idle', 'disconnected']);
export const subscriptionStatusSchema = z.enum(['active', 'expiring_soon', 'none']);

export const deviceSchema: z.ZodType<Device> = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['pump', 'sensor']),
  category: z.enum(['Hawk', 'G70', 'CropX']),
  fuelType: z.enum(['Electric', 'Diesel']).nullable(),
  serial: z.string(),
  imageUrl: z.string(),
  status: deviceStatusSchema,
  powerOn: z.boolean(),
  checkInIntervalMinutes: z.number(),
  lastSyncedAt: z.string().nullable(),
  subscription: z.object({
    status: subscriptionStatusSchema,
    planName: z.string().nullable(),
    expiresAt: z.string().nullable(),
  }),
  warranty: z.object({
    active: z.boolean(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
  }),
  configured: z.boolean(),
  activated: z.boolean(),
  metadata: z.object({
    cropCategory: z.string(),
    serialNumber: z.string(),
    warrantyStart: z.string().nullable(),
    warrantyEnd: z.string().nullable(),
  }),
});

export const deviceListResponseSchema = z.object({
  data: z.array(deviceSchema),
  meta: pageMetaSchema,
});

const flowTrendPointSchema = z.object({ label: z.string(), value: z.number() });

export const deviceTelemetrySchema: z.ZodType<DeviceTelemetry> = z.object({
  deviceId: z.string(),
  totalRuntimeMinutes: z.number(),
  dailyRuntimeMinutes: z.number(),
  externalVoltage: z.number(),
  batteryVoltage: z.number(),
  flowRate: z.number(),
  totalGallons: z.number(),
  flowTrend: z.array(flowTrendPointSchema),
  soil: z
    .object({
      avgMoisturePct: z.number(),
      avgMoistureTrend: z.number(),
      avgTempC: z.number(),
      avgTempTrend: z.number(),
      depthGradient: z.array(z.object({ depthCm: z.number(), percent: z.number() })),
    })
    .nullable(),
});

export const commandSchema: z.ZodType<DeviceCommand> = z.object({
  id: z.string(),
  deviceId: z.string(),
  type: z.enum(['turn_on', 'turn_off', 'stop_timer', 'schedule']),
  state: z.enum(['queued', 'delivered', 'executed', 'failed']),
  createdAt: z.string(),
});

export const activityEventSchema: z.ZodType<ActivityEvent> = z.object({
  id: z.string(),
  deviceId: z.string(),
  type: z.enum(['command', 'subscription', 'alert']),
  title: z.string(),
  detail: z.string(),
  timestamp: z.string(),
  status: z.enum(['success', 'info', 'error']),
});

export const activityListResponseSchema = z.object({
  data: z.array(activityEventSchema),
  meta: pageMetaSchema,
});

export const deviceListResponse = deviceListResponseSchema;
